import type {
  BotConfig,
  BotDifficulty,
  EntityId,
  MapId,
  MatchConfig,
  MatchConnection,
  MatchLifecycleEvent,
  MatchState,
  PlayerId,
  PlayerView,
  SquadArchetype,
  Unsubscribe,
} from '@signalclash/shared';
import {
  BotCommandSource,
  DEFAULT_BOT_DIFFICULTY,
  DEFAULT_MAP_ID,
  HumanCommandSource,
  LocalMatchConnection,
  LocalMatchHost,
  PLAYER_ONE,
  PLAYER_ZERO,
  getMap,
} from '@signalclash/shared';
import Phaser from 'phaser';

import {
  analytics,
  analyticsUrlFromWsEndpoint,
  bootElapsedMs,
} from '../analytics/Analytics.js';
import { AudioBus } from '../audio/AudioBus.js';
import { MusicDirector } from '../audio/MusicDirector.js';
import { Sfx } from '../audio/Sfx.js';
import { Voice } from '../audio/Voice.js';
import { DebugOverlay } from '../debug/DebugOverlay.js';
import { PerfOverlay } from '../debug/PerfOverlay.js';
import { BuildMode } from '../input/BuildMode.js';
import type { InputRouter } from '../input/InputRouter.js';
import { isTouchProfile } from '../input/Profile.js';
import { Selection } from '../input/Selection.js';
import {
  ColyseusMatchConnection,
  createNetworkConnection,
  mintRoomCode,
  type ParsedNetworkBoot,
  parseNetworkBootFromUrl,
  resolveNetworkEndpoint,
  sanitizeRoomCode,
} from '../multiplayer/index.js';
import { UNLOCK_TIERS, getNextTier } from '../save/CosmeticUnlocks.js';
import { applyMatchResult } from '../save/Rank.js';
import { recordSquadBuilt } from '../save/SquadMastery.js';
import { awardMatchXp } from '../save/Xp.js';
import {
  getDailyChallengeProgress,
  getDailyChallenges,
  getTodayUtcDateStr,
} from '../save/DailyChallenges.js';
import { SettingsStore } from '../save/Settings.js';
import {
  clearTelemetry,
  loadRows as loadTelemetryRows,
  recordMatch,
  summarize as summarizeTelemetry,
} from '../save/Telemetry.js';
import { BootScene } from '../scenes/BootScene.js';
import { GameScene } from '../scenes/GameScene.js';
import { MainMenuScene } from '../scenes/MainMenuScene.js';
import { AnnouncerBanner } from '../ui/AnnouncerBanner.js';
import { ArmedBanner } from '../ui/ArmedBanner.js';
import { BuildPanel } from '../ui/BuildPanel.js';
import { CriticalFlipBanner } from '../ui/CriticalFlipBanner.js';
import { DoctrinePicker } from '../ui/DoctrinePicker.js';
import { EndOfMatch } from '../ui/EndOfMatch.js';
import { HudPanel } from '../ui/HudPanel.js';
import { injectHudThemeStyles } from '../ui/hudThemeStyles.js';
import { KillFeed } from '../ui/KillFeed.js';
import { LeaveMatchDialog } from '../ui/LeaveMatchDialog.js';
import { Minimap } from '../ui/Minimap.js';
import { MissionBanner } from '../ui/MissionBanner.js';
import { VictoryBurst, deriveVictoryStats } from '../ui/VictoryBurst.js';
import { MobileActionBar } from '../ui/MobileActionBar.js';
import { MobileStatusStrip } from '../ui/MobileStatusStrip.js';
import { MobileBestOnDesktopBanner } from '../ui/MobileBestOnDesktopBanner.js';
import { OrientationHint } from '../ui/OrientationHint.js';
import { ProfilePanel } from '../ui/ProfilePanel.js';
import { ReconnectBanner } from '../ui/ReconnectBanner.js';
import { RelayUnderAttackAlert } from '../ui/RelayUnderAttackAlert.js';
import { ScreenFlash } from '../ui/ScreenFlash.js';
import { SettingsPanel } from '../ui/SettingsPanel.js';
import { CameraControlsHint } from '../ui/CameraControlsHint.js';
import { IntroOverlay } from '../ui/IntroOverlay.js';
import { Tutorial } from '../ui/Tutorial.js';

import { CrazyGamesSdk } from './CrazyGamesSdk.js';
import { KillEvents } from './KillEvents.js';
import { MATCH_CONFIG_DEFAULT } from './MatchConfig.js';
import { PauseController } from './PauseController.js';

const SEED_FAMILY = 0xc1a5_c1a5;
// Mixing constant: 32-bit step from the golden-ratio constant used by mulberry32 et al.
// Spreads consecutive run indices across the seed space so rematches feel different
// while staying within the same "seed family" the user picked.
const SEED_RUN_STEP = 0x9e3779b1;

// V4.1 — match_checkpoint analytics buckets. Each threshold emits exactly
// once per match when matchSeconds crosses it. The buckets correspond to
// the natural drop-off windows in a Signal Clash match: 30 s (intro
// dismissal complete), 1:00 (first relay should be locked), 2:00 (first
// combat exchanges), 4:00 (mid-match), 6:00 (Signal Storm imminent),
// 8:00 (match length cap; this fires on the final tick before EOM).
const CHECKPOINT_BUCKETS: readonly number[] = [30, 60, 120, 240, 360, 480];

// V1.8 — first-session detection for boot-time default selection. We do
// not import the MainMenuScene helper because that would create a cycle
// (scene imports ClientApp). Reads Telemetry directly; `?firstsession=`
// URL override mirrors MainMenuScene's behavior for QA preview.
function isFirstSessionAtBoot(): boolean {
  if (typeof window !== 'undefined') {
    try {
      const param = new URL(window.location.href).searchParams.get('firstsession');
      if (param === '1') return true;
      if (param === '0') return false;
    } catch {
      // URL parse failure — fall through to telemetry check.
    }
  }
  try {
    return loadTelemetryRows().length === 0;
  } catch {
    return false;
  }
}

export type ClientMode = 'local' | 'network';

// ClientApp orchestrates host/connection/source spine and stands up the Phaser game.
//
// V2 adds a network mode that swaps `LocalMatchConnection` for
// `ColyseusMatchConnection` based on `?room=ABCD` in the page URL. The
// rest of the spine — Renderer, Input, HUD, EndOfMatch, sfx — funnels
// through `humanConnection.onSnapshot` / `humanConnection.onLifecycle`,
// so both modes share the presentation layer. In network mode the
// `LocalMatchHost` instance still exists (cheap; created in the
// constructor) but is never started — the simulation lives on the
// server. The bot pipeline is also unused in network mode.
export class ClientApp {
  // The local host stays around in both modes — costs nothing if unused
  // and lets the constructor stay non-conditional. In network mode we
  // never call `start()` on it.
  readonly host = new LocalMatchHost();
  // `humanConnection` is an interface-typed reference so the per-mode
  // implementation (Local vs Colyseus) is invisible to the rest of the
  // app. The concrete instance is set in the constructor based on the
  // detected boot mode.
  humanConnection: MatchConnection;
  // Bot side is only used in local mode. The fields exist so the
  // construction order stays simple; in network mode they're idle.
  readonly botConnection = new LocalMatchConnection(this.host);
  readonly human = new HumanCommandSource(PLAYER_ZERO);
  bot: BotCommandSource;
  readonly debug = new DebugOverlay();
  // V1.7 (4.19) — togglable perf overlay (F3) for diagnosing the
  // multiplayer-stutter playtest finding. Hidden by default; ships in
  // prod so the user can capture metrics live from CrazyGames.
  readonly perfOverlay = new PerfOverlay();
  readonly selection = new Selection();
  readonly buildMode = new BuildMode();
  readonly hud: HudPanel;
  readonly buildPanel: BuildPanel;
  readonly minimap: Minimap;
  readonly endOfMatch: EndOfMatch;
  readonly doctrinePicker: DoctrinePicker;
  readonly reconnectBanner: ReconnectBanner;
  readonly criticalFlipBanner: CriticalFlipBanner;
  readonly screenFlash: ScreenFlash;
  readonly settingsPanel: SettingsPanel;
  readonly profilePanel: ProfilePanel;
  // V4 release-readiness pass. Juice layer + portal compliance:
  readonly announcerBanner = new AnnouncerBanner();
  readonly killFeed: KillFeed;
  readonly relayUnderAttackAlert: RelayUnderAttackAlert;
  readonly killEvents: KillEvents;
  readonly tutorial = new Tutorial();
  readonly introOverlay = new IntroOverlay();
  readonly cameraControlsHint = new CameraControlsHint();
  readonly mobileBestOnDesktopBanner = new MobileBestOnDesktopBanner();
  // V1.8.4 — accept an externally-constructed SDK so main.ts can run cloud-
  // save hydration BEFORE the rest of ClientApp boots (otherwise save
  // modules read empty localStorage before cloud values arrive). Falls
  // back to a fresh instance when called without an arg (tests, etc.).
  readonly crazyGames: CrazyGamesSdk;
  readonly pause = new PauseController();
  // CrazyGames submission: leave-match confirmation for online matches.
  readonly leaveMatchDialog = new LeaveMatchDialog();
  // Aggregate per-match stats fed into the EOM MVP card. Counted on the
  // client-presentation side only — the simulation doesn't model these.
  private extraStats = { viewerKills: 0, enemyKills: 0, bestStreak: 0 };
  // V4.1 — per-match analytics tracker. Recreated in startMatchChoreography
  // / startNetworkMatch; cleared on match end. The Set<number> records which
  // wall-clock-bucketed checkpoints (30/60/120/240/360/480 s) have already
  // fired so each emits exactly once. Set to null between matches so the
  // snapshot subscriber early-outs cheaply when no match is running.
  //
  // `initialSquadCount` is filled in lazily on the first observed snapshot
  // (config-agnostic; whatever the sim spawned at t=0 is the baseline).
  // Until then `seenFirstSnap === false` and the diff-based events do not
  // fire spurious "first ___" emissions.
  private matchTracker: {
    startMs: number;
    matchAtSec: number;
    seenFirstSnap: boolean;
    firstSquadBuiltEmitted: boolean;
    firstRelayCapturedEmitted: boolean;
    firstSquadLostEmitted: boolean;
    firstKillEmitted: boolean;
    firstAbilityUsedEmitted: boolean;
    firstHubBuiltEmitted: boolean;
    firstDoctrinePickedEmitted: boolean;
    initialSquadCount: number;
    prevSquadCount: number;
    prevHubCount: number;
    checkpointsEmitted: Set<number>;
  } | null = null;
  // Active ClientLoop so PauseController can pause/unpause the host accumulator
  // and the match-start countdown can freeze the sim until "FIGHT".
  private clientLoop: {
    setPaused: (paused: boolean) => void;
    setStartLocked: (locked: boolean) => void;
  } | null = null;
  // V1.9 — objective pulse layer registered by GameScene. The tutorial
  // drives it via setObjectivePulse / clearObjectivePulse on `this`.
  private objectivePulseLayer:
    | { setTarget(x: number, y: number): void; clearTarget(): void }
    | null = null;
  // True while the 3-2-1 countdown is on screen. Human-command emissions are
  // dropped during the lock (see startMatch's `emit` wrapper) and the sim is
  // frozen via clientLoop.setStartLocked, so the world genuinely waits for
  // "FIGHT" instead of letting the player squeeze in early orders.
  private matchStartLocked = false;
  // V3 mobile-only overlays. Hidden via their own CSS @media rules on
  // desktop viewports so they never affect mouse+keyboard play.
  readonly mobileActionBar: MobileActionBar;
  readonly mobileStatusStrip: MobileStatusStrip;
  readonly armedBanner: ArmedBanner;
  // V2 Tempo — per-match sub-goal banner. Picks one of five randomised
  // missions at match start, awards bonus XP on completion.
  readonly missionBanner = new MissionBanner();
  // V2 Tempo — cinematic stats cascade between announcer banner and
  // EOM modal. Auto-clears; no rematch hook needed.
  readonly victoryBurst = new VictoryBurst();
  readonly orientationHint: OrientationHint;
  readonly audio = new AudioBus({ baseUrl: `${import.meta.env.BASE_URL}audio` });
  readonly settings = new SettingsStore();
  readonly sfx: Sfx;
  readonly voice = new Voice(this.audio);
  readonly music = new MusicDirector(this.audio);
  // Match counter threaded into Sfx so the match-start voice rotation picks a
  // different cue per match within a single session. Bumped on every
  // startMatch / startNetworkMatch entry.
  private voiceMatchIndex = 0;

  private game: Phaser.Game | null = null;
  private latest: MatchState | null = null;
  private connectedHuman = false;
  private connectedBot = false;
  private humanSourceStarted = false;
  private botSourceStarted = false;
  private nextRunIndex = 0;
  // V2 Tempo near-event telegraphs — once-per-match rising-edge guards
  // so the announcer doesn't replay these on every snapshot tick.
  private enemyCoreExposedFired = false;
  private coreUnderFireFired = false;
  // V2 Tempo — Underdog Surge spectacle. Fires the moment the trailing
  // player surge first engages for the VIEWER, so the player KNOWS the
  // game's helping them push back.
  private comebackInitiatedFired = false;
  private currentConfig: MatchConfig = MATCH_CONFIG_DEFAULT;
  private currentBotConfig: BotConfig;
  private unsubLifecycle: Unsubscribe | null = null;
  // V1.8 — transport / protocol error stream. Set in startMatch (network
  // mode); torn down in endMatch.
  private unsubConnectionError: Unsubscribe | null = null;
  private mode: ClientMode;
  private networkBoot: ParsedNetworkBoot | null;
  private readonly networkEndpointFallback: string;
  private appSnapshotUnsub: Unsubscribe | null = null;
  // V2.5+ Reconnect-Bot opt-in. Owned by `MainMenuScene` (the lobby
  // toggle); ClientApp threads it to `ColyseusMatchConnection.connect`
  // as a per-client join option. Default OFF — a client that never
  // saw the toggle (older builds, automated tooling) opts out.
  private allowBotReconnectFill = false;
  // Slot assigned by the server in network mode (PLAYER_ZERO in local
  // mode by construction). Read by `GameScene` at Renderer
  // construction time and by `applyAssignedPlayerId` to update HUD /
  // build panel / minimap / Sfx / doctrine picker / human source.
  private assignedPlayerId: PlayerId = PLAYER_ZERO;
  // GameScene registers its InputRouter here so the mobile action bar +
  // armed-banner can call into trigger* hooks (keyboard parity for on-screen
  // buttons). Null in the menu scene and during transitions.
  private inputRouter: InputRouter | null = null;
  private matchStartCountdownPlayed = false;

  constructor(crazyGames?: CrazyGamesSdk) {
    // V1.8.4 — use the externally-hydrated SDK if main.ts provided one;
    // otherwise fall back to a fresh instance (legacy / test path).
    this.crazyGames = crazyGames ?? new CrazyGamesSdk();
    // Persisted bot difficulty seeds the first match. SettingsStore loads from
    // localStorage; absent / corrupt values fall back to DEFAULT_BOT_DIFFICULTY.
    // V1.8 — first-session players (no telemetry rows yet) get Recruit
    // regardless of the global default. CrazyGames stats showed Tactical
    // crushes new RTS players who bounce on a first-match loss.
    const persistedDifficulty =
      this.settings.get().botDifficulty ??
      (isFirstSessionAtBoot() ? 'recruit' : DEFAULT_BOT_DIFFICULTY);
    this.currentBotConfig = { difficulty: persistedDifficulty };
    this.bot = new BotCommandSource(PLAYER_ONE, this.currentBotConfig);

    // Mode detection lives here so MainMenuScene + every UI piece can
    // read a stable `app.getMode()` from creation onward. V2.5 endpoint
    // resolution priority (see networkBoot.ts top of file):
    //   1. ?endpoint= URL override (handled inside the parser).
    //   2. VITE_NETWORK_ENDPOINT (Vite-inlined from .env.production).
    //   3. defaultEndpointFromOrigin(originHref).
    const href = typeof window !== 'undefined' ? window.location.href : '';
    // Cast `import.meta.env` to a known shape for the env var read.
    // Vite types it as `Record<string, any>` by default but we want the
    // call site to be precise about what we're pulling out.
    const envEndpoint = (import.meta.env as { VITE_NETWORK_ENDPOINT?: string })
      .VITE_NETWORK_ENDPOINT;
    const originHref = typeof window !== 'undefined' ? window.location.href : '';
    const fallback = resolveNetworkEndpoint({
      envEndpoint,
      originHref: originHref === '' ? 'http://localhost' : originHref,
    });
    this.networkEndpointFallback = fallback;
    this.networkBoot = parseNetworkBootFromUrl(href, fallback);
    this.mode = this.networkBoot !== null ? 'network' : 'local';

    // V4.1 — wire the analytics emitter. URL is derived from the
    // resolved network endpoint REGARDLESS of mode: even local-vs-bot
    // plays post analytics to the same Fly.io server. This means the
    // funnel covers 100% of plays, not just the few-percent network
    // plays the V2.5 emitter covered.
    const analyticsUrl = analyticsUrlFromWsEndpoint(fallback);
    analytics.setUrl(analyticsUrl);
    analytics.setEnabledCheck(() => this.settings.get().analyticsEnabled);
    analytics.install();
    // Branch on the boot kind. Quick Play targets the
    // `signal_clash_quick` room type with no roomCode (Colyseus pairs
    // the next two waiting clients); private rooms keep using
    // `signal_clash` with `?room=ABCD` matchmaker pairing.
    if (this.networkBoot === null) {
      this.humanConnection = new LocalMatchConnection(this.host);
    } else if (this.networkBoot.kind === 'quickplay') {
      this.humanConnection = new ColyseusMatchConnection({
        endpoint: this.networkBoot.endpoint,
        roomType: 'signal_clash_quick',
      });
    } else {
      this.humanConnection = new ColyseusMatchConnection({
        endpoint: this.networkBoot.endpoint,
        roomCode: this.networkBoot.roomCode,
      });
    }

    this.hud = new HudPanel({
      playerId: PLAYER_ZERO,
      human: this.human,
      selection: this.selection,
      getRouter: () => this.inputRouter,
      buildMode: this.buildMode,
    });
    this.buildPanel = new BuildPanel({
      playerId: PLAYER_ZERO,
      human: this.human,
      buildMode: this.buildMode,
    });
    this.minimap = new Minimap({ playerId: PLAYER_ZERO });
    this.sfx = new Sfx(this.audio, PLAYER_ZERO);
    this.sfx.setVoice(this.voice);
    // Selection sounds: derived purely from the selection model so any caller
    // that updates Selection (InputRouter, MobileActionBar, BuildPanel) gets
    // the cue for free. The `same`-check inside Selection drops re-selects
    // before the listener fires, so toggling on/off the same entity doesn't
    // spam clicks. Subscription returns the current selection synchronously,
    // which is initially null — we skip that first call.
    //
    // V1 voice pack: when the new selection is a viewer-owned squad, look up
    // the archetype from the latest snapshot so Sfx can route the
    // archetype-specific selection voice line. Non-squad selections / enemy
    // squads / squads that no longer exist all fall through to the generic
    // SFX click without a voice line.
    let selectionInitialNotified = false;
    this.selection.subscribe((sel) => {
      if (!selectionInitialNotified) {
        selectionInitialNotified = true;
        return;
      }
      if (sel === null) return;
      const archetype = this.viewerSquadArchetype(sel);
      this.sfx.playSelection(sel.kind, archetype);
    });
    this.endOfMatch = new EndOfMatch({
      onRematch: () => {
        void this.restartMatch();
        this.startGameScene();
      },
      onBackToMenu: () => {
        void this.endMatch();
        this.startMainMenuScene();
      },
    });
    this.doctrinePicker = new DoctrinePicker({
      playerId: PLAYER_ZERO,
      human: this.human,
    });
    this.reconnectBanner = new ReconnectBanner();
    this.criticalFlipBanner = new CriticalFlipBanner(PLAYER_ZERO);
    this.killFeed = new KillFeed(PLAYER_ZERO);
    this.relayUnderAttackAlert = new RelayUnderAttackAlert(PLAYER_ZERO);
    this.killEvents = new KillEvents(PLAYER_ZERO);
    this.screenFlash = new ScreenFlash();
    this.settingsPanel = new SettingsPanel({
      audio: this.audio,
      settings: this.settings,
      onBotDifficultyChange: (d) => {
        this.setBotDifficulty(d);
      },
      onClose: () => {
        // Settings panel is modal-but-stackable — closing returns the player to
        // whichever scene mounted it. The scene itself owns "back to menu" /
        // "resume" semantics; this hook is a no-op anchor.
      },
    });
    this.mobileActionBar = new MobileActionBar({
      selection: this.selection,
      buildMode: this.buildMode,
      human: this.human,
      getRouter: () => this.inputRouter,
      playerId: PLAYER_ZERO,
    });
    // V5++ — mobile-only status strip pinned above the action bar. Hosts
    // selection info (squad portrait + HP, Core/Hub title + stats + queue,
    // or Select-All CTA) so the bottom-of-screen becomes one coherent dock
    // instead of a floating bottom-right panel + center-bottom bar.
    this.mobileStatusStrip = new MobileStatusStrip({
      selection: this.selection,
      getRouter: () => this.inputRouter,
      playerId: PLAYER_ZERO,
    });
    this.armedBanner = new ArmedBanner({
      getRouter: () => this.inputRouter,
      buildMode: this.buildMode,
    });
    this.orientationHint = new OrientationHint();
    this.profilePanel = new ProfilePanel();
    injectHudThemeStyles();
    // V2 Tempo — every squad the player queues ticks the per-archetype
    // SquadMastery counter. Lives at construction time so a rematch
    // doesn't need to re-attach; the observer survives connection
    // restarts.
    this.human.setProduceObserver((archetype) => {
      recordSquadBuilt(archetype, 1);
    });
  }

  // GameScene calls this after constructing its InputRouter so the mobile
  // action bar / armed banner can drive it. Passing null on scene-shutdown
  // disarms the trigger* hooks and clears the banner.
  setInputRouter(router: InputRouter | null): void {
    this.inputRouter = router;
    this.armedBanner.refresh();
  }

  // GameScene calls this once it constructs its ClientLoop so the
  // PauseController can pause the host accumulator on visibility-loss.
  // Passing null on scene-shutdown releases the pause hook.
  registerClientLoop(
    loop: {
      setPaused: (paused: boolean) => void;
      setStartLocked: (locked: boolean) => void;
    } | null,
  ): void {
    this.clientLoop = loop;
    // A loop registered mid-countdown (rare — only if the scene rebuilds
    // while the lock is held) inherits the current lock state so it can't
    // tick past the freeze.
    if (loop !== null) loop.setStartLocked(this.matchStartLocked);
  }

  // V1.9 — register the per-scene objective pulse layer. The tutorial
  // calls `setObjectivePulse(worldX, worldY)` / `clearObjectivePulse()`
  // on ClientApp; we forward to the live pulse layer. The indirection
  // is required because Tutorial.ts is constructed once (in ClientApp's
  // ctor) but GameScene rebuilds on every match — we can't capture a
  // permanent reference at Tutorial construction time.
  registerObjectivePulse(
    pulse: { setTarget(x: number, y: number): void; clearTarget(): void } | null,
  ): void {
    this.objectivePulseLayer = pulse;
    // Keep the Tutorial's hooks in sync with the live layer. Wired
    // here (rather than in the Tutorial ctor) because the layer is
    // created per scene mount while the Tutorial is constructed once.
    if (pulse === null) {
      this.tutorial.setObjectiveHooks(null, null);
    } else {
      this.tutorial.setObjectiveHooks(
        (x, y) => pulse.setTarget(x, y),
        () => pulse.clearTarget(),
      );
    }
  }

  setObjectivePulse(worldX: number, worldY: number): void {
    this.objectivePulseLayer?.setTarget(worldX, worldY);
  }

  clearObjectivePulse(): void {
    this.objectivePulseLayer?.clearTarget();
  }

  getMode(): ClientMode {
    return this.mode;
  }

  getNetworkBoot(): ParsedNetworkBoot | null {
    return this.networkBoot;
  }

  // V1.8 — exposed so MainMenuScene can run a /health preflight before
  // surfacing the Online 1v1 button. The fallback (env / origin-derived)
  // is the same string used to instantiate ColyseusMatchConnection.
  getNetworkEndpoint(): string {
    return this.networkEndpointFallback;
  }

  parseNetworkBootFromHref(href: string): ParsedNetworkBoot | null {
    return parseNetworkBootFromUrl(href, this.networkEndpointFallback);
  }

  activateNetworkBoot(boot: ParsedNetworkBoot): void {
    const previous = this.humanConnection;
    void previous.disconnect().catch(() => undefined);
    this.connectedHuman = false;
    this.mode = 'network';
    this.networkBoot = boot;
    this.humanConnection = createNetworkConnection(boot);
    this.applyAssignedPlayerId(PLAYER_ZERO);
    this.bindAppSnapshotStream();
  }

  activateLocalBoot(): void {
    const previous = this.humanConnection;
    void previous.disconnect().catch(() => undefined);
    this.connectedHuman = false;
    this.mode = 'local';
    this.networkBoot = null;
    this.humanConnection = new LocalMatchConnection(this.host);
    this.applyAssignedPlayerId(PLAYER_ZERO);
    this.bindAppSnapshotStream();
    this.crazyGames.hideInviteButton();
    this.music.playMenu();
  }

  private bindAppSnapshotStream(): void {
    this.appSnapshotUnsub?.();
    this.appSnapshotUnsub = this.humanConnection.onSnapshot((snap: PlayerView) => {
      this.latest = snap;
      this.debug.setTick(snap.tick);
      this.perfOverlay.setTick(snap.tick);
      this.perfOverlay.recordSnapshot(snap.tick);
      this.hud.update(snap);
      this.buildPanel.update(snap);
      this.minimap.update(snap);
      this.endOfMatch.observeSnapshot(snap);
      this.doctrinePicker.observeSnapshot(snap);
      this.mobileActionBar.update(snap);
      this.mobileStatusStrip.update(snap);
      this.armedBanner.refresh();
      this.tutorial.observeSnapshot(snap);
      this.missionBanner.observeSnapshot(snap);
      this.checkCoreHpTelegraphs(snap);
      this.trackMatchMilestones(snap);
    });
  }

  // V2 Tempo near-event telegraphs. Each fires once per match on the
  // rising edge of an HP threshold so the player gets a single big "go
  // for it" or "panic" beat instead of a recurring nag. Both gated on
  // matchActive so we don't fire stale alerts during boot, EOM, or the
  // pre-match countdown.
  private checkCoreHpTelegraphs(snap: MatchState): void {
    if (!this.matchActive) return;
    const viewerId = this.assignedPlayerId;
    const enemyId = viewerId === 0 ? 1 : 0;
    let viewerCoreRatio = 1;
    let enemyCoreRatio = 1;
    for (const core of Object.values(snap.cores)) {
      const ratio = core.maxHp > 0 ? core.hp / core.maxHp : 1;
      if (core.ownerId === viewerId) viewerCoreRatio = ratio;
      else if (core.ownerId === enemyId) enemyCoreRatio = ratio;
    }
    if (!this.enemyCoreExposedFired && enemyCoreRatio > 0 && enemyCoreRatio < 0.35) {
      this.enemyCoreExposedFired = true;
      this.announcerBanner.pushEnemyCoreExposed();
    }
    if (!this.coreUnderFireFired && viewerCoreRatio > 0 && viewerCoreRatio < 0.35) {
      this.coreUnderFireFired = true;
      this.announcerBanner.pushCoreUnderFire();
    }
    // V2 Tempo — Underdog Surge engaged for the viewer. Reads the
    // public snapshot flag (UnderdogSurgeActive[playerId]) that
    // EconomySystem sets when the income boost fires. Once-per-match
    // rising edge so the banner doesn't replay each tick while the
    // surge stays on.
    if (!this.comebackInitiatedFired && snap.underdogSurgeActive?.[viewerId] === true) {
      this.comebackInitiatedFired = true;
      this.announcerBanner.pushComebackInitiated();
    }
  }

  // Cleared on every match-start so the rising-edge guards re-arm per
  // match. Called from the same hooks that reset matchTracker.
  private resetCoreHpTelegraphs(): void {
    this.enemyCoreExposedFired = false;
    this.coreUnderFireFired = false;
    this.comebackInitiatedFired = false;
  }

  // V4.1 — per-snapshot funnel emitter. Cheap early-out when matchTracker
  // is null (between matches or before startMatch finishes). Each event
  // emits at most once per match because the tracker's `*Emitted` flags
  // gate on the rising edge. Checkpoint emissions cross fixed wall-clock
  // thresholds (30/60/120/240/360/480 s of matchSeconds) so the dashboard
  // can bucket dropoff at standard cuts without per-second resolution.
  private trackMatchMilestones(snap: PlayerView): void {
    const t = this.matchTracker;
    if (t === null) return;
    const viewerId = this.assignedPlayerId;
    const player = snap.players[viewerId];
    if (player === undefined) return;
    t.matchAtSec = snap.matchSeconds;
    const matchSec = snap.matchSeconds;
    const elapsedMs = (): number => Math.max(0, Math.round((matchSec) * 1000));
    // Lazy baseline init — the FIRST observed snapshot determines what
    // "no production yet" looks like (config-agnostic; respects any future
    // pre-spawn changes). Diff-based events are gated on seenFirstSnap.
    if (!t.seenFirstSnap) {
      t.seenFirstSnap = true;
      t.initialSquadCount = player.squadCount;
      t.prevSquadCount = player.squadCount;
      let hubs = 0;
      for (const b of Object.values(snap.buildings)) {
        if (b.kind === 'production_hub' && b.ownerId === viewerId) hubs += 1;
      }
      t.prevHubCount = hubs;
    }

    // Checkpoint buckets — every threshold crossed since last snap fires.
    // The CrazyGames dashboard reports averages; these per-bucket counts
    // let us split into 30s / 60s / 2m / 4m / 6m / 8m cohorts and see the
    // dropoff shape directly.
    for (const bucket of CHECKPOINT_BUCKETS) {
      if (matchSec >= bucket && !t.checkpointsEmitted.has(bucket)) {
        t.checkpointsEmitted.add(bucket);
        let viewerRelays = 0;
        for (const node of Object.values(snap.controlNodes)) {
          if (node.ownerId === viewerId) viewerRelays += 1;
        }
        analytics.emit({
          kind: 'match_checkpoint',
          atSec: bucket,
          viewerRelays,
          viewerSquads: player.squadCount,
          viewerSignal: Math.round(player.signal),
        });
      }
    }

    // first_squad_built — squadCount climbed past the pre-spawned initial
    // squad count. The match starts with one Recon per side already on the
    // field, so the rising edge of `squadCount > initialSquadCount` is the
    // first PRODUCED squad.
    if (
      !t.firstSquadBuiltEmitted &&
      player.squadCount > t.initialSquadCount
    ) {
      t.firstSquadBuiltEmitted = true;
      // Snapshot doesn't tell us which archetype was just built. Pick the
      // newest viewer-owned squad as a best-effort label.
      let newestArchetype = 'recon';
      for (const sq of Object.values(snap.squads)) {
        if (sq.ownerId !== viewerId) continue;
        newestArchetype = sq.archetype;
      }
      analytics.emit({
        kind: 'first_squad_built',
        elapsedMs: elapsedMs(),
        archetype: newestArchetype,
      });
    }

    // first_relay_captured — any viewer-owned ControlNode appears.
    if (!t.firstRelayCapturedEmitted) {
      for (const node of Object.values(snap.controlNodes)) {
        if (node.ownerId === viewerId) {
          t.firstRelayCapturedEmitted = true;
          analytics.emit({ kind: 'first_relay_captured', elapsedMs: elapsedMs() });
          break;
        }
      }
    }

    // first_squad_lost — squad count dipped below the rolling max. We
    // track the previous-tick count so a death between snapshots is the
    // rising edge.
    if (!t.firstSquadLostEmitted && player.squadCount < t.prevSquadCount) {
      t.firstSquadLostEmitted = true;
      analytics.emit({ kind: 'first_squad_lost', elapsedMs: elapsedMs() });
    }
    t.prevSquadCount = player.squadCount;

    // first_ability_used — any viewer-owned squad has a non-null channel
    // OR a positive cooldown (mirrors the tutorial's "ability" predicate
    // so the two fire together for the same player action).
    if (!t.firstAbilityUsedEmitted) {
      for (const sq of Object.values(snap.squads)) {
        if (sq.ownerId !== viewerId) continue;
        if (sq.channelingAbility !== null) {
          t.firstAbilityUsedEmitted = true;
          analytics.emit({
            kind: 'first_ability_used',
            elapsedMs: elapsedMs(),
            abilityId: sq.channelingAbility.abilityId,
          });
          break;
        }
        if (sq.abilityCooldown > 0) {
          t.firstAbilityUsedEmitted = true;
          analytics.emit({
            kind: 'first_ability_used',
            elapsedMs: elapsedMs(),
            // Cooldown alone doesn't tell us which ability fired; fall
            // back to a generic label so the count is still meaningful.
            abilityId: 'unknown',
          });
          break;
        }
      }
    }

    // first_hub_built — production_hub building owned by viewer.
    if (!t.firstHubBuiltEmitted) {
      let hubs = 0;
      for (const b of Object.values(snap.buildings)) {
        if (b.kind === 'production_hub' && b.ownerId === viewerId) hubs += 1;
      }
      if (hubs > t.prevHubCount) {
        t.firstHubBuiltEmitted = true;
        analytics.emit({ kind: 'first_hub_built', elapsedMs: elapsedMs() });
      }
      t.prevHubCount = hubs;
    }

    // first_doctrine_picked — player's doctrineId becomes non-null. The
    // doctrine window opens at 3:00 in V1.5; if the player misses the
    // pick the system auto-assigns at window close, so this event still
    // fires (just later).
    if (!t.firstDoctrinePickedEmitted && player.doctrineId !== null) {
      t.firstDoctrinePickedEmitted = true;
      analytics.emit({
        kind: 'first_doctrine_picked',
        elapsedMs: elapsedMs(),
        doctrineId: player.doctrineId,
      });
    }
  }

  private resetMatchTracker(): void {
    this.matchTracker = {
      startMs: Date.now(),
      matchAtSec: 0,
      seenFirstSnap: false,
      firstSquadBuiltEmitted: false,
      firstRelayCapturedEmitted: false,
      firstSquadLostEmitted: false,
      firstKillEmitted: false,
      firstAbilityUsedEmitted: false,
      firstHubBuiltEmitted: false,
      firstDoctrinePickedEmitted: false,
      initialSquadCount: 0,
      prevSquadCount: 0,
      prevHubCount: 0,
      checkpointsEmitted: new Set<number>(),
    };
  }

  // V4.1 — fire audio_unlocked at most once per session. The AudioContext
  // is gated until a user gesture; we observe the first time ensureRunning
  // is invoked from the document-level click delegate. Tracked separately
  // from any per-match state so a refresh-then-replay still surfaces the
  // unlock event in the dashboard.
  private audioUnlockedEmitted = false;
  // V4.1 — match-active tracker for match_left_early. Flipped true when
  // host.start fires synchronously inside startMatchChoreography (or when
  // network mode's startNetworkMatch finishes). Flipped false in the
  // match_ended lifecycle handler. The pagehide listener reads this to
  // decide whether a tab close counts as an early exit.
  private matchActive = false;
  // V4.1 — drop-time anchor for network_disconnect's reconnectMs. Set
  // when opponent_disconnected fires; cleared in opponent_reconnected.
  private lastDropAtMs: number | null = null;
  private lastDropAtSec = 0;

  // Delegated UI feedback: a single document-level click/hover listener plays
  // ui_click / ui_hover for ANY <button> in the DOM (menu, build panel, HUD
  // produce, doctrine picker, EOM, mobile action bar, settings…). The 40 ms
  // per-name throttle inside Sfx absorbs accidental double-clicks and rapid
  // mouse-over sweeps across button clusters, so the player gets satisfying
  // tactile feedback without each panel having to thread `sfx` through its
  // constructor. Keyboard-only triggers (e.g. 'B' for build toggle) call
  // `playUi*` directly from InputRouter where the DOM listener can't see them.
  private wireUiDelegation(): void {
    if (typeof document === 'undefined') return;
    // try/catch the inner handlers: these run on EVERY click anywhere in
    // the DOM, including the menu's first user gesture. A throw here
    // (audio context creation failing on a hostile browser, sfx routing
    // failing because the bus isn't ready) would propagate to the
    // document-level click pipeline and the global error handler. With
    // a CR of 30%, every additional defended throw on the first-click
    // path matters.
    document.body.addEventListener('click', (e) => {
      try {
        const target = e.target;
        if (target instanceof HTMLElement && target.closest('button') !== null) {
          // Click is a user gesture — guarantee the AudioContext is resumed
          // BEFORE the play call so the first click of the session isn't lost
          // to a still-suspended context.
          this.audio.ensureRunning();
          if (!this.audioUnlockedEmitted) {
            this.audioUnlockedEmitted = true;
            analytics.emit({ kind: 'audio_unlocked', elapsedMs: bootElapsedMs() });
          }
          this.sfx.playUiClick();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ui-delegation] click handler threw', err);
      }
    });
    document.body.addEventListener('mouseover', (e) => {
      try {
        const target = e.target;
        if (target instanceof HTMLElement && target.closest('button') !== null) {
          this.sfx.playUiHover();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ui-delegation] hover handler threw', err);
      }
    });
  }

  async start(): Promise<void> {
    this.wireUiDelegation();
    // CrazyGames loadingStart must precede sdkGameLoadingStop (called by
    // MainMenuScene.create once the menu is interactive). The SDK script
    // is lazy-loaded inside `ready()` and can take 100s of ms; firing
    // sdkGameLoadingStart here queues the intent so the portal sees a
    // clean start-then-stop pair regardless of which resolves first.
    this.crazyGames.sdkGameLoadingStart();
    // Loading-stop watchdog. The portal's loading overlay stays up
    // until `sdkGameLoadingStop` reaches the SDK. MainMenuScene.create()
    // fires it on the normal path, but if anything between here and
    // there hangs (frozen Phaser init, dead-locked await, unrecoverable
    // throw the BootScene catch doesn't cover), the stop signal never
    // arrives and the portal classifies the session as "stuck loading".
    // 20 s is well past any legitimate boot (median ~1 s, p99 ~5 s);
    // firing here after a real freeze still gives the player a chance
    // to see whatever surface did render instead of an infinite portal
    // spinner. The watchdog is a one-shot — once the menu mounts and
    // fires its own loadingStop, the second call inside the SDK
    // wrapper is harmless (the SDK collapses duplicate stops).
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        this.crazyGames.sdkGameLoadingStop();
      }, 20_000);
    }
    // CrazyGames SDK lazy-load. Resolves immediately if not on the portal.
    // The .then() body handles invite + instant-multiplayer routing once
    // the real SDK is bound. A trailing .catch keeps any synchronous throw
    // (future SDK shape change, navigation error) from surfacing as an
    // unhandled rejection.
    // V4.2 — invite-while-in-game listener. The complementary case to
    // the boot-time inviteParams check below: when a player is ALREADY
    // running the game and clicks an invite notification from a
    // friend, the SDK fires this listener with the friend's room
    // params. We treat it the same as a fresh invite — sanitize and
    // navigate via `?room=` so the existing network-boot path picks
    // it up. The unsubscribe is intentionally not retained; the
    // listener lives for the entire app lifetime.
    this.crazyGames.addJoinRoomListener((inviteParams) => {
      if (typeof window === 'undefined') return;
      const roomRaw = inviteParams.room;
      if (typeof roomRaw !== 'string') return;
      const room = sanitizeRoomCode(roomRaw);
      if (room === '') return;
      const url = new URL(window.location.href);
      url.searchParams.delete('quickplay');
      url.searchParams.set('room', room);
      // Full navigation: ClientApp's mode + networkBoot are computed
      // in the constructor, so an in-place swap would need to plumb
      // through more state than is worth maintaining. Replace honours
      // back-button semantics (the previous URL was the one the
      // player was already on).
      window.location.replace(url.toString());
    });

    void this.crazyGames
      .ready()
      .then(() => {
        if (typeof window !== 'undefined' && this.networkBoot === null) {
          // V4.2 — invitee boot. Per the multiplayer SDK docs the
          // canonical source is `inviteParams` (the whole bag). The
          // V3 `getInviteParam(key)` accessor still works as a
          // fallback in case the SDK build exposes one but not the
          // other.
          //
          // Sanitize BEFORE writing the URL. If the SDK returns a
          // value with characters our parser rejects, the previous
          // code wrote the unclean value to ?room=,
          // parseNetworkBootFromUrl would strip it back to null, the
          // next render would call getInviteParam again (same unclean
          // value), and the client would redirect-loop forever —
          // every short session counted as a crash in the portal's
          // churn metric. sanitizeRoomCode returns '' for anything
          // that won't parse, and we skip the redirect in that case.
          const inviteParams = this.crazyGames.getInviteParams();
          const inviteRoomRaw =
            inviteParams?.room ?? this.crazyGames.getInviteParam('room');
          const inviteRoom =
            inviteRoomRaw !== null && inviteRoomRaw !== undefined
              ? sanitizeRoomCode(inviteRoomRaw)
              : '';
          if (inviteRoom !== '') {
            const url = new URL(window.location.href);
            url.searchParams.set('room', inviteRoom);
            window.location.replace(url.toString());
            return;
          }
          // Party-leader path (CrazyGames multiplayer requirement): when
          // isInstantMultiplayer is true the player launched from a "Play
          // with friends" entry point and must land directly in a fresh
          // multiplayer room. Mint a code, push it into the URL, and the
          // reload re-boots into network mode via parseNetworkBootFromUrl.
          if (this.crazyGames.isInstantMultiplayer()) {
            const url = new URL(window.location.href);
            url.searchParams.delete('quickplay');
            url.searchParams.set('room', mintRoomCode(5));
            window.location.replace(url.toString());
          }
        }
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[crazygames] ready/invite-routing failed', err);
      });
    // Show mobile "best on desktop" hint on the menu. Self-checks for
    // touch profile + already-dismissed; no-ops on desktop / repeat visits.
    this.mobileBestOnDesktopBanner.showIfMobile();
    // App-level subscription follows the active connection. Local/menu boots
    // start with LocalMatchConnection; in-place Quick Play / Invite swaps it
    // to Colyseus without reloading the page, so this binding is refreshable.
    this.bindAppSnapshotStream();

    // PauseController wiring — visibility-change → ClientLoop.setPaused.
    this.pause.subscribe((paused) => {
      this.clientLoop?.setPaused(paused);
    });
    // Kill-events streams into the announcer (multikill streaks +
    // first blood) and the kill feed. Aggregate counters feed the MVP.
    this.killEvents.onKill((e) => {
      this.killFeed.handleKill(e);
      if (e.killerOwnerId === this.assignedPlayerId) {
        this.extraStats.viewerKills += 1;
        // V4.1 — first_kill milestone. Tracked outside the snapshot
        // pump because kills are KillEvents-derived, not state-diffed.
        const t = this.matchTracker;
        if (t !== null && !t.firstKillEmitted) {
          t.firstKillEmitted = true;
          analytics.emit({
            kind: 'first_kill',
            elapsedMs: Math.round(t.matchAtSec * 1000),
            // KillEvent only carries the VICTIM archetype (per-projectile
            // attribution doesn't exist in the sim). The dashboard reading
            // is "the player's first elimination was a {victim} squad".
            archetype: e.victimArchetype,
          });
        }
      } else {
        this.extraStats.enemyKills += 1;
      }
    });
    this.killEvents.onFirstBlood(() => {
      this.announcerBanner.pushFirstBlood();
    });
    this.killEvents.onStreak((e) => {
      if (e.killerOwnerId !== this.assignedPlayerId) return;
      if (e.tier === 'single') return;
      this.announcerBanner.pushStreak(e.tier);
      if (e.count > this.extraStats.bestStreak) this.extraStats.bestStreak = e.count;
    });

    this.hud.hide();
    this.buildPanel.hide();
    this.minimap.hide();
    this.endOfMatch.hide();
    this.doctrinePicker.hide();
    this.mobileActionBar.hide();
    this.mobileStatusStrip.hide();
    this.armedBanner.hide();
    this.killFeed.hide();
    this.relayUnderAttackAlert.hide();
    this.tutorial.hide();
    this.introOverlay.dispose();
    this.cameraControlsHint.dismiss();

    // V4.1 — pagehide-during-active-match → match_left_early. The
    // emitter's own install() also listens to pagehide for queue
    // flushing, but its handler runs purely on the emitter side; we
    // need a separate listener to inspect matchActive. Both run on the
    // same event, both are no-ops when no match is in flight.
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => {
        if (!this.matchActive) return;
        analytics.emit({
          kind: 'match_left_early',
          atSec: this.latest?.matchSeconds ?? 0,
          reason: 'tab_close',
        });
        this.matchActive = false;
      });
    }

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: 'game',
      // iOS Safari (standalone-PWA boot, dynamic viewport collapsing mid-
      // launch) occasionally reports innerWidth/innerHeight as 0 for the
      // first frame. Some WebGL drivers reject 0-area surfaces and Phaser
      // can't recover — the next BootScene texture create returns null
      // and the game soft-locks on a black canvas. A 1-px floor lets
      // Phaser allocate a real surface; the RESIZE scale mode will
      // expand to the real viewport as soon as window.innerWidth
      // reports a sane value (next layout frame).
      width: Math.max(1, window.innerWidth),
      height: Math.max(1, window.innerHeight),
      backgroundColor: '#0b0d10',
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.NO_CENTER,
      },
      render: {
        antialias: true,
        pixelArt: false,
      },
      // V3 mobile: ensure Phaser allocates extra pointer slots so two-finger
      // pinch/pan gestures both deliver events. Default is 2 (mouse + 1
      // touch); we need 3 (mouse + 2 simultaneous touches).
      input: { activePointers: 3 },
      scene: [BootScene, MainMenuScene, GameScene],
    });
    this.game.registry.set('app', this);
    // V4.1 — surface WebGL context-loss to analytics. On the CrazyGames
    // crash-rate KPI, a lost context typically presents as a stuck
    // black canvas (the canvas keeps existing, draws fail silently).
    // We hook the DOM event directly because Phaser doesn't surface
    // 'contextlost' on game.events — the renderer absorbs it.
    try {
      const canvas = this.game.canvas;
      if (canvas !== undefined) {
        canvas.addEventListener('webglcontextlost', () => {
          // Per the WebGL spec, calling preventDefault() here causes
          // the browser to attempt context restoration. Phaser doesn't
          // yet recover gracefully on every device — a restored context
          // typically leaves the renderer with stale GL objects and
          // surfaces as a corrupted scene. We let the context stay
          // lost (player refreshes) and just surface the event so the
          // dashboard sees the crash signature.
          analytics.emit({ kind: 'webgl_context_lost' });
        });
      }
    } catch {
      // Canvas access can throw on a half-initialised game; the next
      // restart will retry. Non-fatal.
    }
    // V1.5 telemetry — expose a dev-console handle so a playtester can run
    // `__sigclash_telemetry()` to dump rows or `__sigclash_telemetrySummary()`
    // for the aggregate. Pure read; never used by gameplay code.
    if (typeof window !== 'undefined') {
      const w = window as unknown as Record<string, unknown>;
      w.__sigclash_telemetry = (): unknown => loadTelemetryRows();
      w.__sigclash_telemetrySummary = (): unknown => summarizeTelemetry(loadTelemetryRows());
      w.__sigclash_telemetryClear = (): void => clearTelemetry();
      // Trailer-director handles — expose the app and Phaser game so the
      // docs/trailer-director.js console script can drive the game
      // programmatically to produce a reproducible gameplay recording.
      // Read-only surface; gameplay code never reads these window keys.
      w.__sigclash_app = this;
      w.__sigclash_game = this.game;
    }
  }

  setBotDifficulty(difficulty: BotDifficulty): void {
    if (difficulty === this.currentBotConfig.difficulty) return;
    this.currentBotConfig = { difficulty };
  }

  getBotDifficulty(): BotDifficulty {
    return this.currentBotConfig.difficulty;
  }

  // V2.5+ Reconnect-Bot opt-in. The lobby checkbox calls this; the
  // value is read at `connect()` time inside `startNetworkMatch`. Once
  // the match has started the flag is fixed for the session — there is
  // no in-match toggle (V3+ scope per docs/prompts/07_reconnect_bot.md
  // "DO NOT, IN THIS PASS").
  setAllowBotReconnectFill(allow: boolean): void {
    this.allowBotReconnectFill = allow;
  }

  getAllowBotReconnectFill(): boolean {
    return this.allowBotReconnectFill;
  }

  // V2.5+ network slot assignment. Read by `GameScene` to construct
  // the renderer with the right viewer slot. Defaults to PLAYER_ZERO
  // for local mode (the constructor's value, never overwritten).
  getAssignedPlayerId(): PlayerId {
    return this.assignedPlayerId;
  }

  // Propagate the server-assigned slot to every UI/source piece that
  // hardcoded PLAYER_ZERO at construction time. Called from
  // `startNetworkMatch` once after the `assigned` Colyseus message
  // resolves. Idempotent — safe to call with the same id again.
  private applyAssignedPlayerId(playerId: PlayerId): void {
    this.assignedPlayerId = playerId;
    this.human.setPlayerId(playerId);
    this.hud.setPlayerId(playerId);
    this.buildPanel.setPlayerId(playerId);
    this.minimap.setPlayerId(playerId);
    this.sfx.setPlayerId(playerId);
    this.doctrinePicker.setPlayerId(playerId);
    this.mobileActionBar.setPlayerId(playerId);
    this.mobileStatusStrip.setPlayerId(playerId);
    this.criticalFlipBanner.setViewerId(playerId);
    this.killFeed.setViewerId(playerId);
    this.relayUnderAttackAlert.setViewerId(playerId);
    this.killEvents.setViewerId(playerId);
    this.endOfMatch.setViewerId(playerId);
    this.tutorial.setViewerId(playerId);
  }

  // V1.5 second map: GameScene + Minimap read which map to draw obstacles
  // from before any snapshot lands. The simulation also bakes mapId onto
  // MatchState; ClientApp is the source of truth for "what the next match
  // will use".
  setMapId(mapId: MapId): void {
    if (this.currentConfig.mapId === mapId) return;
    this.currentConfig = { ...this.currentConfig, mapId };
  }

  getCurrentMapId(): MapId | undefined {
    return this.currentConfig.mapId;
  }

  async startMatch(
    configArg?: MatchConfig,
    botConfig: BotConfig = this.currentBotConfig,
  ): Promise<void> {
    // V1.5+ — preserve currentConfig (which carries the live mapId set via
    // setMapId() from the menu picker) when the caller doesn't pass an
    // explicit config. Before this change, the no-arg call from
    // MainMenuScene reset currentConfig to MATCH_CONFIG_DEFAULT and threw
    // away the user's map choice, so every match started on Foundry
    // regardless of which map button was selected. restartMatch() already
    // passes this.currentConfig explicitly, so its behaviour is unchanged.
    const config = configArg ?? this.currentConfig;
    this.currentConfig = config;
    this.currentBotConfig = botConfig;
    this.audio.ensureRunning();

    if (this.mode === 'network') {
      await this.startNetworkMatch();
      return;
    }

    // Rebuild the bot when the requested difficulty differs from the active one.
    // Stops the previous source first so we don't leak its snapshot listener.
    if (this.bot.difficulty !== botConfig.difficulty) {
      if (this.botSourceStarted) {
        this.bot.stop();
        this.botSourceStarted = false;
      }
      this.bot = new BotCommandSource(PLAYER_ONE, botConfig);
    }
    const seed = this.mintSeed();
    // Order matters: connections must be registered as observers before host.start, so
    // the host's initial-state broadcast reaches the HUD/renderer immediately. If we
    // started the host first, the initial snapshot would be dropped (no observers yet)
    // and the screen would stay blank until the first 50 ms sim tick.
    if (!this.connectedHuman) {
      await this.humanConnection.connect(PLAYER_ZERO);
      this.connectedHuman = true;
    }
    if (!this.connectedBot) {
      await this.botConnection.connect(PLAYER_ONE);
      this.connectedBot = true;
    }

    this.endOfMatch.reset();
    this.doctrinePicker.reset();
    // V1.8 — suppress the doctrine modal on the player's FIRST match.
    // The sim still opens the window + auto-assigns at the close tick;
    // the player just doesn't get a red-countdown modal at the exact
    // moment CrazyGames stats show they were already bouncing.
    this.doctrinePicker.setSuppressForFirstMatch(loadTelemetryRows().length === 0);
    // Reset the flag BEFORE subscribeLifecycle so the 'started' handler
    // (fired synchronously inside host.start below) can schedule the
    // countdown exactly once. Without this reset a rematch would see a
    // stale `true` and the guard would suppress the countdown entirely.
    this.matchStartCountdownPlayed = false;
    // Clear any leftover start-lock from a prior match (e.g. the player
    // returned to menu mid-countdown). The 'started' handler will set it
    // back to true synchronously inside host.start below.
    this.matchStartLocked = false;
    this.clientLoop?.setStartLocked(false);
    this.subscribeLifecycle();

    // Music attaches BEFORE Sfx so its `match_ended` handler runs first
    // and fades out the bed loop before Sfx plays the post-match jingle.
    this.music.detach();
    this.music.attach(this.humanConnection);
    this.sfx.detach();
    this.voice.resetForMatch();
    this.voiceMatchIndex += 1;
    this.sfx.setMatchIndex(this.voiceMatchIndex);
    this.sfx.attach(this.humanConnection, (h) => this.humanConnection.onLifecycle(h));

    // V1.7 — host.start() emits the 'started' lifecycle event synchronously,
    // which fires the 3-2-1 countdown banner, the "SIGNAL LIVE" text, the
    // "Core online" announcer voice cue, the match-start SFX, and broadcasts
    // the initial snapshot. All of those are presentation effects that run
    // off real-time setTimeout / audio playback timers, not the sim tick —
    // so pausing the sim does NOT stop them. To suppress those during the
    // pre-match intro overlay we defer host.start (and everything that
    // depends on its initial snapshot broadcast) until the overlay dismisses.
    // If the intro was already seen, the deferred block runs immediately so
    // returning players experience no extra delay.
    const startMatchChoreography = (): void => {
      this.host.start(seed, config);

      if (this.humanSourceStarted) this.human.stop();
      if (this.botSourceStarted) this.bot.stop();
      this.human.start({
        emit: (cmd) => {
          // Match-start countdown lock: drop human commands until "FIGHT".
          // Single chokepoint covers every input path (clicks, hotkeys,
          // mobile action bar, build panel, doctrine picker).
          if (this.matchStartLocked) return;
          this.humanConnection.send(cmd);
        },
        onSnapshot: (h) => this.humanConnection.onSnapshot(h),
      });
      this.bot.start({
        emit: (cmd) => this.botConnection.send(cmd),
        onSnapshot: (h) => this.botConnection.onSnapshot(h),
      });
      this.humanSourceStarted = true;
      this.botSourceStarted = true;

      this.hud.show();
      this.buildPanel.show();
      this.minimap.show();
      this.mobileActionBar.show();
      this.mobileStatusStrip.show();
      this.killFeed.show();
      this.killFeed.clear();
      this.relayUnderAttackAlert.show();
      this.armedBanner.refresh();
      if (isTouchProfile()) this.orientationHint.showInMatch();
      // V4 juice + portal hooks: reset MVP counters, attach kill-event /
      // relay-alert detectors. The 3-2-1-FIGHT countdown is scheduled by
      // the 'started' lifecycle handler in subscribeLifecycle (fired
      // synchronously by host.start above), so we do NOT call it again here.
      this.extraStats = { viewerKills: 0, enemyKills: 0, bestStreak: 0 };
      this.endOfMatch.setExtraStats(this.extraStats);
      this.killEvents.detach();
      this.killEvents.attach(this.humanConnection);
      this.relayUnderAttackAlert.detach();
      this.relayUnderAttackAlert.attach(this.humanConnection);
      this.announcerBanner.clear();
      this.crazyGames.gameplayStart();
      // V1.8 — surface Leave Match in local mode too. CrazyGames analytics
      // count tab-close sessions as "ended early" which hurts retention
      // measurement. A clean Leave Match button gives the player an
      // explicit exit; emitMatchLeftEarly tags the row with
      // reason='menu_btn' so the dashboard can separate true abandons.
      this.leaveMatchDialog.attach(() => {
        void this.endMatch().then(() => this.startMainMenuScene());
      });
      this.pause.attach({ host: this.host, audio: this.audio, isNetwork: false });
      this.tutorial.setViewerId(this.assignedPlayerId);
      this.tutorial.beginIfFirstMatch();
      this.cameraControlsHint.show();
      // V4.1 — start the per-match analytics tracker AFTER the sim's
      // initial state has been broadcast (host.start() above fires the
      // 'started' lifecycle and pushes the initial snapshot
      // synchronously). The tracker's first-snap baseline locks in on
      // the next snapshot that lands.
      this.resetMatchTracker();
      this.resetCoreHpTelegraphs();
      this.missionBanner.start(this.assignedPlayerId);
      this.matchActive = true;
    };

    // Pre-match comprehension card. Local mode only — first-time players
    // overwhelmingly land here, and the modal would compete with the
    // Colyseus room's already-running timer in network mode. The overlay
    // pre-empts the entire match-start choreography (no countdown / voice /
    // banner plays underneath it) by deferring host.start until dismissal.
    const overlayShown = this.introOverlay.beginIfFirstSession({
      onDismiss: () => startMatchChoreography(),
    });
    if (!overlayShown) {
      // Returning player (or DOM-less environment) — match starts right
      // away, identical to the V1.6 flow.
      startMatchChoreography();
    }
  }

  // Network-mode start. Joins the Colyseus room, wires lifecycle + sfx
  // through humanConnection (the room forwards the host's events), and
  // starts only the human source. The local host/bot pipeline is unused.
  //
  // Slot assignment: the SERVER decides which Colyseus room slot this
  // client occupies (V2.5+ behaviour after fixing the V2 bug where
  // every client tried to claim PLAYER_ZERO). After `connect()`
  // resolves, `getAssignedPlayerId()` returns the server-assigned
  // slot. We propagate that to the renderer / HUD / fog overlay /
  // command source BEFORE starting the source so the very first
  // snapshot pump renders against the correct viewer slot.
  private async startNetworkMatch(): Promise<void> {
    if (!this.connectedHuman) {
      // Thread the V2.5+ Reconnect-Bot opt-in into the per-client join
      // options. The shared `MatchConnection.connect` interface accepts
      // an optional bag; LocalMatchConnection ignores it (and is never
      // hit in network mode anyway), ColyseusMatchConnection forwards
      // it as `allowBotReconnectFill: true` in `joinOrCreate(roomType,
      // joinOptions)`.
      //
      // The first arg (PLAYER_ZERO) is a placeholder for the shared
      // interface contract; ColyseusMatchConnection ignores it and
      // waits for the server's `assigned` message instead.
      await this.humanConnection.connect(PLAYER_ZERO, {
        allowBotReconnectFill: this.allowBotReconnectFill,
      });
      this.connectedHuman = true;
    }
    // Propagate the server-assigned slot to every UI / source piece
    // that hardcoded PLAYER_ZERO in its constructor. A null assigned
    // id (server bug, timeout) keeps the PLAYER_ZERO default — the
    // game still renders, just from the wrong perspective if this
    // client was actually slot 1.
    if (this.humanConnection instanceof ColyseusMatchConnection) {
      const assigned = this.humanConnection.getAssignedPlayerId();
      if (assigned !== null) this.applyAssignedPlayerId(assigned);
    }

    this.endOfMatch.reset();
    this.doctrinePicker.reset();
    // V1.8 — same first-match modal suppression as the local path above.
    this.doctrinePicker.setSuppressForFirstMatch(loadTelemetryRows().length === 0);
    this.subscribeLifecycle();
    this.subscribeConnectionError();

    // Music attaches BEFORE Sfx so its `match_ended` handler runs first
    // and fades out the bed loop before Sfx plays the post-match jingle.
    this.music.detach();
    this.music.attach(this.humanConnection);
    this.sfx.detach();
    this.voice.resetForMatch();
    this.voiceMatchIndex += 1;
    this.sfx.setMatchIndex(this.voiceMatchIndex);
    this.sfx.attach(this.humanConnection, (h) => this.humanConnection.onLifecycle(h));

    if (this.humanSourceStarted) this.human.stop();
    this.human.start({
      emit: (cmd) => {
        // Same start-lock chokepoint as local mode. In network mode the
        // server sim keeps ticking through the countdown (out of scope
        // for a client-only fix), but blocking emissions here at least
        // prevents the player from squeezing in early orders.
        if (this.matchStartLocked) return;
        this.humanConnection.send(cmd);
      },
      onSnapshot: (h) => this.humanConnection.onSnapshot(h),
    });
    this.humanSourceStarted = true;

    this.hud.show();
    this.buildPanel.show();
    this.minimap.show();
    this.mobileActionBar.show();
    this.mobileStatusStrip.show();
    this.killFeed.show();
    this.killFeed.clear();
    this.relayUnderAttackAlert.show();
    this.armedBanner.refresh();
    if (isTouchProfile()) this.orientationHint.showInMatch();
    // V4 juice + portal hooks: identical wiring to local mode. Pause
    // controller still pauses audio in network mode; the simulation is
    // server-authoritative so the host accumulator doesn't apply.
    this.extraStats = { viewerKills: 0, enemyKills: 0, bestStreak: 0 };
    this.endOfMatch.setExtraStats(this.extraStats);
    this.killEvents.detach();
    this.killEvents.attach(this.humanConnection);
    this.relayUnderAttackAlert.detach();
    this.relayUnderAttackAlert.attach(this.humanConnection);
    this.announcerBanner.clear();
    this.matchStartCountdownPlayed = false;
    this.matchStartLocked = false;
    this.clientLoop?.setStartLocked(false);
    this.crazyGames.gameplayStart();
    // CrazyGames submission: show the leave-match button + register
    // beforeunload guard (both managed by LeaveMatchDialog).
    this.leaveMatchDialog.attach(() => {
      void this.endMatch().then(() => this.startMainMenuScene());
    });
    this.pause.attach({ host: this.host, audio: this.audio, isNetwork: true });
    this.tutorial.setViewerId(this.assignedPlayerId);
    this.tutorial.beginIfFirstMatch();
    this.cameraControlsHint.show();
    // V4.1 — start the per-match analytics tracker. See the local-mode
    // path above for the seenFirstSnap rationale.
    this.resetMatchTracker();
    this.resetCoreHpTelegraphs();
    this.missionBanner.start(this.assignedPlayerId);
    this.matchActive = true;
  }

  // Lifecycle subscription is shared between local and network startMatch
  // paths. Routes match_ended → EOM + telemetry, and routes the V2
  // opponent_* events → reconnect banner. The banner stays a no-op in
  // local mode because LocalMatchHost never emits opponent_* events.
  private subscribeLifecycle(): void {
    this.unsubLifecycle?.();
    this.unsubLifecycle = this.humanConnection.onLifecycle((event: MatchLifecycleEvent) => {
      switch (event.kind) {
        case 'started':
          // V4.2 — both slots filled, the match is starting. Tell the
          // CrazyGames platform the room is no longer joinable so the
          // native invite button hides and the friends drawer drops
          // the room from the "joinable" list. Idempotent / no-op when
          // not on portal or for local matches.
          if (this.mode === 'network') {
            this.crazyGames.updateRoom({ isJoinable: false });
          }
          if (!this.matchStartCountdownPlayed) {
            // Freeze the world for the duration of "3 ... 2 ... 1 ...":
            // local sim stops ticking (clientLoop.setStartLocked) and
            // human-command emissions are dropped by the gate in the
            // emit wrapper below. Released the instant "FIGHT" pushes
            // (network mode still has a running server sim, so the
            // input gate is the only thing that matters there).
            this.matchStartLocked = true;
            this.clientLoop?.setStartLocked(true);
            this.announcerBanner.scheduleMatchStartCountdown(() => {
              this.matchStartLocked = false;
              this.clientLoop?.setStartLocked(false);
            });
            this.matchStartCountdownPlayed = true;
          }
          // V2.5 anonymous analytics — fire ONCE per match per client
          // when the server's `started` lifecycle reaches us.
          // match_completed / outcome_recorded are server-emitted to
          // avoid double-counting in HvH (docs/14_CHANGELOG.md V2.5
          // Slice 4). In local mode the analytics URL is null and
          // emit() no-ops.
          this.emitMatchStartedAnalytics();
          break;
        // V4 polish — big-stage announcer banners for the three
        // rule-changing mid-match moments. Each lifecycle event already
        // has a dedicated SFX cue + (for storm/surge) a sticky HUD
        // banner; the announcer is the cinematic *moment*, fired once.
        case 'overtime_started':
          this.announcerBanner.pushOvertime();
          break;
        case 'signal_storm_started':
          this.announcerBanner.pushSignalStorm();
          break;
        case 'signal_surge_started':
          this.announcerBanner.pushSignalSurge();
          break;
        case 'match_ended':
          this.reconnectBanner.hide();
          // V4.1 — local-mode match_completed analytics. In network mode
          // the server emits this from SignalClashRoom.onLifecycle so
          // human-vs-human matches aren't double-counted. Local-vs-bot
          // play has no server emitter, so the client is the only source
          // of truth — without this, ~95% of plays (CrazyGames default
          // is local) had no completion event in the dashboard.
          if (this.mode === 'local') {
            const matchSeconds = this.latest?.matchSeconds ?? 0;
            const won = event.outcome.winner === this.assignedPlayerId;
            analytics.emit({
              kind: 'match_completed',
              outcomeKind: event.outcome.kind,
              matchSeconds,
              viewerKills: this.extraStats.viewerKills,
              enemyKills: this.extraStats.enemyKills,
              peakStreak: this.extraStats.bestStreak,
              mapId: this.currentConfig.mapId ?? DEFAULT_MAP_ID,
              difficulty: this.currentBotConfig.difficulty,
              won,
              ...(this.latest?.players[this.assignedPlayerId]?.doctrineId !== null &&
              this.latest?.players[this.assignedPlayerId]?.doctrineId !== undefined
                ? { doctrineId: this.latest.players[this.assignedPlayerId]!.doctrineId! }
                : {}),
            });
          }
          // EOM-shown is wired by the EndOfMatch modal itself via its
          // analytics callback in show()/scheduleShow(); see EndOfMatch.ts.
          // The match-end milestone tracker is cleared here so the next
          // match starts with a clean tracker the moment startMatch
          // mints a new one.
          this.matchTracker = null;
          // V4.1 — match completed naturally; clear the active flag so a
          // subsequent pagehide doesn't misclassify a post-match tab
          // close as match_left_early.
          this.matchActive = false;
          // V1.5+ Slice 3 — drop any in-flight critical-flip banners so
          // the EOM modal isn't visually competing with a stale "SIGNAL
          // LINK CUT" countdown. The lifecycle bus stops emitting once
          // outcome is set anyway.
          this.criticalFlipBanner.clear();
          // V2 Tempo — hide the mission badge so the EOM modal owns
          // the screen. The completion celebration plays in-match before
          // this lands; an in-flight mission just hides cleanly.
          this.missionBanner.onMatchEnded();
          // V4 release pass — push the final aggregate counters into the
          // EOM card so the MVP row reflects the kills/streak from this
          // session, then close out the juice / portal session.
          this.endOfMatch.setExtraStats(this.extraStats);
          // Climactic V/D burst. Clear queued in-match streak banners
          // first so the result tier owns the screen; the lifted
          // z-index sits the banner above the EOM modal. Modal show is
          // delayed ~1.6s so the player gets the moment before the
          // stats card fades up under it.
          this.announcerBanner.clear();
          // V2 Tempo — match-end punch flash. Tinted cyan on win, red on
          // loss. Bigger / longer than the critical-flip flash. Fires
          // FIRST (before the announcer banner) so the screen flashes
          // the second the match resolves; the announcer banner then
          // pops out of that flash.
          const viewerWon = event.outcome.winner === this.assignedPlayerId;
          this.screenFlash.punch({ won: viewerWon });
          this.announcerBanner.pushMatchResult(viewerWon);
          // V2 Tempo — cinematic stats burst between the result-tier
          // announcer and the EOM modal. The burst takes ~1.7s; we shift
          // the modal show to ~2.4s so the burst lands first, peaks,
          // then the modal fades up underneath.
          const burstMs = this.victoryBurst.show(deriveVictoryStats({
            finalState: this.latest,
            viewerId: this.assignedPlayerId,
            viewerKills: this.extraStats.viewerKills,
            won: viewerWon,
          }));
          this.endOfMatch.scheduleShow(event.outcome, this.latest, Math.max(1600, burstMs + 600));
          this.killFeed.hide();
          this.relayUnderAttackAlert.hide();
          this.tutorial.dispose();
          this.introOverlay.dispose();
          this.crazyGames.gameplayStop();
          if (event.outcome.winner === this.assignedPlayerId) this.crazyGames.happytime();
          this.pause.detach();
          // CrazyGames submission: match ended naturally — detach the
          // leave-match dialog (which also removes the beforeunload guard)
          // so no warning fires on normal post-match navigation.
          this.leaveMatchDialog.detach();
          // V1.5 telemetry — record a paste-friendly row for playtest
          // analysis. No-op when localStorage is unavailable (tests, SSR).
          // NOTE: this is the localStorage write only. The server-side
          // analytics endpoint receives match_completed from the
          // server itself (see SignalClashRoom.onLifecycle) — we do
          // NOT post it from here.
          if (this.latest !== null) {
            // V4.1 — snapshot pre-record state so we can detect crossings.
            // Cosmetic-unlock tiers and daily-challenge completions both
            // fire here because the row write is the moment match-count
            // / daily-challenge progress changes.
            const before = loadTelemetryRows();
            const beforeCount = before.length;
            const today = getTodayUtcDateStr();
            const dailyDefs = getDailyChallenges(today);
            const beforeProgress = getDailyChallengeProgress(
              dailyDefs,
              before,
              today,
            );
            recordMatch(this.latest);
            const after = loadTelemetryRows();
            const afterCount = after.length;
            // Tier crossings — emit one event per tier crossed (the
            // pathological "you crossed 5 AND 15 in the same row" case
            // is unreachable in practice but the loop handles it).
            // V1.8 — route the highest crossed tier into the EndOfMatch
            // modal so the player sees a celebration card. The earlier
            // implementation emitted analytics but never surfaced the
            // unlock to the player; the retention nudge was wasted.
            let highestCrossed: typeof UNLOCK_TIERS[number] | null = null;
            for (const tier of UNLOCK_TIERS) {
              if (beforeCount < tier.matchThreshold && afterCount >= tier.matchThreshold) {
                analytics.emit({
                  kind: 'unlock_reached',
                  tierId: tier.title.toLowerCase().replace(/\s+/g, '_'),
                  totalMatches: afterCount,
                });
                highestCrossed = tier;
              }
            }
            this.endOfMatch.setPendingUnlock(highestCrossed);
            // V1.9 — next-tier preview hook. The "X more matches to
            // unlock <Title>" chip sits below the unlock card so the
            // player who DIDN'T cross a boundary still gets a visible
            // carrot at the Rematch decision moment. `afterCount` is
            // post-record, so the chip math reflects "after this
            // match". When the player has already unlocked the final
            // tier, getNextTier returns null and the chip stays hidden.
            const next = getNextTier(afterCount);
            this.endOfMatch.setNextTier(
              next,
              next === null ? 0 : Math.max(0, next.matchThreshold - afterCount),
            );
            // V1.9 — XP award. Computed AFTER recordMatch so the row
            // count used by level-up gates is consistent. The award
            // function persists the new XP value itself (cloud-sync via
            // the CrazyGames Data Module install hook).
            const won = event.outcome.winner === this.assignedPlayerId;
            const award = awardMatchXp(
              this.latest,
              this.assignedPlayerId,
              this.extraStats.viewerKills,
              won,
            );
            this.endOfMatch.setXpAward(award);
            // V2 Tempo — apply rank ELO update. The EOM rank chip
            // animates the ±delta and pulses gold on a tier-up. Network
            // matches use a fixed-1200 opponent until matchmaking is
            // ELO-aware (Rank.ts has the rationale).
            const rankUpdate = applyMatchResult({
              won,
              mode: this.mode,
              difficulty: this.currentBotConfig.difficulty,
            });
            this.endOfMatch.setRankUpdate(rankUpdate);
            // Daily challenges — emit progress + completion events when
            // a challenge moves forward or completes this match.
            const afterProgress = getDailyChallengeProgress(dailyDefs, after, today);
            for (let i = 0; i < afterProgress.length; i++) {
              const next = afterProgress[i]!;
              const prev = beforeProgress[i];
              if (prev === undefined) continue;
              if (next.current !== prev.current) {
                analytics.emit({
                  kind: 'daily_challenge_progress',
                  challengeId: next.def.id,
                  before: prev.current,
                  after: next.current,
                });
              }
              if (next.done && !prev.done) {
                analytics.emit({
                  kind: 'daily_challenge_completed',
                  challengeId: next.def.id,
                });
              }
            }
          }
          break;
        case 'opponent_disconnected':
          // V2 default label — "Opponent disconnected — Xs to reconnect".
          // Reset in case a previous bot_reconnect_filled left the
          // banner template in the V2.5+ wording (we don't currently
          // emit both for the same drop, but resetting keeps the
          // behaviour idempotent if the order of events ever shifts).
          this.reconnectBanner.resetLabelTemplate();
          this.reconnectBanner.show(event.closesAtMs);
          // V4.1 — network_disconnect analytics. Drop anchor for the
          // reconnectMs computation; emit the drop event immediately so
          // the dashboard can count drops even if the opponent never
          // reconnects (V2 forfeit path).
          this.lastDropAtMs = Date.now();
          this.lastDropAtSec = this.latest?.matchSeconds ?? 0;
          analytics.emit({
            kind: 'network_disconnect',
            atSec: this.lastDropAtSec,
            reconnected: false,
          });
          break;
        case 'opponent_reconnected':
          this.reconnectBanner.hide();
          // Restore the default for the NEXT disconnect — bot_reconnect_*
          // template state is per-drop, not sticky.
          this.reconnectBanner.resetLabelTemplate();
          // V4.1 — emit the reconnect side of the pair. Dashboard joins
          // the two via atSec proximity. reconnectMs is the wall-clock
          // gap, capped at the reconnect window (90 s) so a stale anchor
          // can't produce a garbage value.
          if (this.lastDropAtMs !== null) {
            const reconnectMs = Math.min(90_000, Date.now() - this.lastDropAtMs);
            analytics.emit({
              kind: 'network_disconnect',
              atSec: this.lastDropAtSec,
              reconnected: true,
              reconnectMs,
            });
            this.lastDropAtMs = null;
          }
          break;
        case 'bot_reconnect_filled':
          // V2.5+ Reconnect-Bot — both players opted in. The dropped
          // human can still come back inside the same 90 s window, so
          // the banner shows them a "you can still take back over"
          // countdown. The remaining player sees the same banner —
          // "Bot playing for opponent" gives them an honest readout
          // (Rule 21).
          this.reconnectBanner.setLabelTemplate(
            (s) => `Bot playing for opponent — ${s}s to take back over`,
          );
          this.reconnectBanner.show(event.closesAtMs);
          break;
        case 'bot_reconnect_removed':
          // Bot stopped (either reconnect succeeded or the match ended
          // naturally). Hide the banner; the opponent_reconnected /
          // match_ended handlers do their own UI work.
          this.reconnectBanner.hide();
          this.reconnectBanner.resetLabelTemplate();
          break;
        case 'critical_flip':
          // V1.5+ Slice 3 — the banner filters by `victimPlayerId ===
          // viewerId` internally, so we route every event in. The screen
          // flash only fires for the high-severity reasons (`link_cut`
          // and `relay_isolated`) and only when the viewer is the
          // victim — `mini_turret_dark` is too frequent (each deploy
          // has a one-tick grace where it could re-trigger) and
          // `reconnected` is good news; neither warrants a flash.
          this.criticalFlipBanner.handleEvent(event);
          if (
            event.victimPlayerId === this.assignedPlayerId &&
            (event.reason === 'link_cut' || event.reason === 'relay_isolated')
          ) {
            this.screenFlash.flash();
          }
          break;
        default:
          break;
      }
    });
  }

  // V1.8 — surface transport / protocol errors (captive portal, mobile
  // proxy drops, server crashes) as a visible banner with a manual retry
  // instead of leaving the player staring at a frozen frame. The retry
  // re-runs `startMatch` with the same boot params; the server is
  // expected to honor a rejoin via Colyseus's reconnection token. If it
  // can't (room disposed), the next startMatch attempt cleanly fails
  // through the existing lobby-error UI.
  private subscribeConnectionError(): void {
    this.unsubConnectionError?.();
    this.unsubConnectionError = null;
    if (!(this.humanConnection instanceof ColyseusMatchConnection)) return;
    const conn = this.humanConnection;
    this.unsubConnectionError = conn.onError((error) => {
      analytics.emit({
        kind: 'client_error',
        source: 'phaser',
        message: `[network ${error.kind}] ${error.code} ${error.message}`.slice(0, 200),
      });
      // Fire-and-forget — the banner is purely advisory. Players who
      // ignore it and refresh the tab end up in the normal lobby path.
      this.reconnectBanner.showError(
        `Connection lost — ${error.message}`,
        () => {
          this.reconnectBanner.hide();
          void this.startMatch().catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.warn('[reconnect retry] startMatch failed', err);
            this.reconnectBanner.showError(
              'Reconnect failed — refresh the page to try again.',
            );
          });
        },
      );
    });
  }

  // V2.5 analytics emit, extended in V4.1:
  //   - Fires for BOTH local and network matches (V2.5 only fired network).
  //   - Carries extended match-shape fields so the dashboard can split by
  //     map / difficulty / first-match vs returning without joining tables.
  //   - Uses the singleton emitter rather than the one-shot beacon helper —
  //     opt-out gating now lives inside the emitter.
  private emitMatchStartedAnalytics(): void {
    const roomType: 'signal_clash' | 'signal_clash_quick' | 'local' =
      this.networkBoot === null
        ? 'local'
        : this.networkBoot.kind === 'quickplay'
          ? 'signal_clash_quick'
          : 'signal_clash';
    const totalMatchesBefore = loadTelemetryRows().length;
    analytics.emit({
      kind: 'match_started',
      roomType,
      mapId: this.currentConfig.mapId ?? DEFAULT_MAP_ID,
      difficulty: this.currentBotConfig.difficulty,
      isFirstMatch: totalMatchesBefore === 0,
      totalMatchesBefore,
    });
  }

  async restartMatch(): Promise<void> {
    if (this.mode === 'network') {
      // Network rematch is a server concern (the room is disposed after
      // match_ended). For V2 we route Rematch → back to menu; the player
      // can copy-link to start a fresh room.
      await this.endMatch();
      this.startMainMenuScene();
      return;
    }
    this.host.stop();
    this.endOfMatch.hide();
    this.buildMode.cancel();
    await this.startMatch(this.currentConfig);
  }

  async endMatch(): Promise<void> {
    // V4.1 — if the match was still in-flight, surface the explicit-leave
    // event. The eom_menu_clicked / eom_rematch_clicked paths flip the
    // flag false BEFORE calling endMatch via the lifecycle handler, so
    // this only fires for true mid-match aborts (LeaveMatchDialog "Leave"
    // button, or any future direct endMatch caller).
    if (this.matchActive) {
      analytics.emit({
        kind: 'match_left_early',
        atSec: this.latest?.matchSeconds ?? 0,
        reason: 'menu_btn',
      });
      this.matchActive = false;
    }
    // CrazyGames submission: always detach the leave-match dialog
    // (which removes beforeunload guard + button) on explicit endMatch.
    this.leaveMatchDialog.detach();
    if (this.mode === 'local') {
      this.host.stop();
    }
    if (this.humanSourceStarted) {
      this.human.stop();
      this.humanSourceStarted = false;
    }
    if (this.mode === 'local' && this.botSourceStarted) {
      this.bot.stop();
      this.botSourceStarted = false;
    }
    this.unsubLifecycle?.();
    this.unsubLifecycle = null;
    this.unsubConnectionError?.();
    this.unsubConnectionError = null;
    this.sfx.detach();
    this.music.detach();
    this.audio.stopVoice();
    this.endOfMatch.hide();
    this.doctrinePicker.hide();
    this.reconnectBanner.hide();
    this.criticalFlipBanner.clear();
    this.hud.hide();
    this.buildPanel.hide();
    this.minimap.hide();
    this.buildMode.cancel();
    this.selection.clear();
    this.latest = null;
    this.mobileActionBar.hide();
    this.mobileStatusStrip.hide();
    this.armedBanner.hide();
    this.orientationHint.hide();
    // V4 release-readiness pass — release everything wired in startMatch.
    this.killEvents.detach();
    this.relayUnderAttackAlert.detach();
    this.killFeed.hide();
    this.killFeed.clear();
    this.relayUnderAttackAlert.clear();
    this.announcerBanner.clear();
    this.tutorial.dispose();
    this.introOverlay.dispose();
    this.crazyGames.gameplayStop();
    this.pause.detach();
    if (this.mode === 'network' && this.connectedHuman) {
      // V4.2 — release the platform room registration BEFORE the
      // Colyseus disconnect so the CrazyGames friends drawer / native
      // invite button reflect "left" state immediately. The order
      // doesn't matter for correctness (both are best-effort), but
      // doing leftRoom first means a same-tick re-render of the
      // platform UI is clean.
      this.crazyGames.leftRoom();
      // Disconnect from the Colyseus room so the next session starts
      // clean. We swallow disconnect errors — the server may have
      // already disposed the room (e.g. opponent left first).
      await this.humanConnection.disconnect().catch(() => undefined);
      this.connectedHuman = false;
    }
  }

  startGameScene(): void {
    if (this.game === null) return;
    const sm = this.game.scene;
    if (sm.isActive('MainMenu')) sm.stop('MainMenu');
    if (sm.isActive('Game')) {
      sm.stop('Game');
    }
    sm.start('Game');
  }

  startMainMenuScene(): void {
    if (this.game === null) return;
    const sm = this.game.scene;
    if (sm.isActive('Game')) sm.stop('Game');
    if (sm.isActive('MainMenu')) sm.stop('MainMenu');
    sm.start('MainMenu');
  }

  getLatestSnapshot(): MatchState | null {
    return this.latest;
  }

  getWorldSize(): { width: number; height: number } {
    // V1.5+ — world size follows the currently-loaded map. Crucible is
    // 3000×2400; Foundry / Vault / Canyon remain 2000×2000.
    const mapId = this.currentConfig.mapId ?? DEFAULT_MAP_ID;
    const m = getMap(mapId);
    return { width: m.width, height: m.height };
  }

  private mintSeed(): number {
    const idx = this.nextRunIndex;
    this.nextRunIndex += 1;
    return (SEED_FAMILY ^ Math.imul(idx, SEED_RUN_STEP)) >>> 0;
  }

  // Looks up the archetype of a viewer-owned squad selection so the voice
  // layer can route per-class chatter. Returns undefined for non-squad refs,
  // enemy squads, or squads that no longer exist in the latest snapshot.
  private viewerSquadArchetype(
    sel: { kind: 'squad' | 'building' | 'core'; id: EntityId },
  ): SquadArchetype | undefined {
    if (sel.kind !== 'squad') return undefined;
    if (this.latest === null) return undefined;
    const sq = this.latest.squads[sel.id];
    if (sq === undefined) return undefined;
    if (sq.ownerId !== this.assignedPlayerId) return undefined;
    return sq.archetype;
  }

  // InputRouter calls this on move / attack orders so the voice layer can
  // narrate per-archetype. Resolves the archetype of the FIRST id in the
  // ordered squad list (the primary in a multi-select group); voice doesn't
  // need to enumerate the whole group because we play one line, not N.
  // Returns undefined if the squad is enemy-owned or no longer exists.
  resolveOrderArchetype(ids: EntityId[]): SquadArchetype | undefined {
    if (ids.length === 0) return undefined;
    if (this.latest === null) return undefined;
    const sq = this.latest.squads[ids[0]!];
    if (sq === undefined) return undefined;
    if (sq.ownerId !== this.assignedPlayerId) return undefined;
    return sq.archetype;
  }
}
